import { push } from '@takeshape/redux-little-router';
import { getSavingErrorMessage } from 'app/common/utils/handle-error';
import { showMessageSaga } from 'app/messages/saga';
import { getPermission } from 'app/permissions/selectors';
import { getResourceString } from 'app/permissions/util';
import { getValues } from 'app/schema-form/selectors';
import { onFormSuccess } from 'app/schema-form/util/on-submit';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getRoutineSaga } from '../common/utils/new-sagas.js';
import { GET_PROJECT } from '../project/action-types.js';
import { getProjectPath } from '../project/index.js';
import { getAccessPolicyFromForm } from './access-policy.js';
import {
  getRoleListRoutine,
  removeRoleRoutine,
  type SaveRolePayload,
  saveRoleRoutine,
  updateRoleRoutine
} from './actions';
import { createRole, getRoleList, removeRole, updateRole } from './api.js';
import { USER_ROLE_FORM } from './constants.js';
import { getRoleId } from './selectors.js';

export function* getRoleListSaga(): SagaIterator {
  const { result } = yield call(getRoleList);
  return result.items;
}

export function* saveRoleSaga(newRole: SaveRolePayload): SagaIterator {
  return yield call(createRole, { input: newRole });
}

export function* updateRoleSaga(newRole: SaveRolePayload): SagaIterator {
  return yield call(updateRole, { input: newRole });
}

export function* removeRoleSaga(payload: string): SagaIterator {
  return yield call(removeRole, { name: payload });
}

export function* handleProjectRoles(): SagaIterator {
  const canListRoles = yield select(getPermission, {
    action: 'role:list',
    resource: getResourceString('role')
  });
  if (canListRoles) {
    yield put(getRoleListRoutine.requested());
  }
}

export function* handleRoleSave(): SagaIterator {
  const roleId = yield select(getRoleId);
  const roleForm = yield select(getValues, { formName: USER_ROLE_FORM });
  const newRole = {
    ...roleForm,
    permissions: getAccessPolicyFromForm(roleForm.permissions)
  };
  if (!roleId || roleId === 'create') {
    yield put(saveRoleRoutine.requested(newRole));
  } else {
    yield put(updateRoleRoutine.requested(newRole));
  }
}

export function* postSaveRedirect(): SagaIterator {
  const projectPath = yield select(getProjectPath);
  yield put(push(`${projectPath}/roles`));
  yield put(getRoleListRoutine.requested());
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(onFormSuccess(USER_ROLE_FORM), handleRoleSave),
    takeLatest(getRoleListRoutine.REQUESTED, getRoutineSaga(getRoleListRoutine, getRoleListSaga)),
    takeLatest(saveRoleRoutine.REQUESTED, getRoutineSaga(saveRoleRoutine, saveRoleSaga)),
    takeLatest(updateRoleRoutine.REQUESTED, getRoutineSaga(updateRoleRoutine, updateRoleSaga)),
    takeLatest(removeRoleRoutine.REQUESTED, getRoutineSaga(removeRoleRoutine, removeRoleSaga)),
    takeLatest(`${GET_PROJECT}_REQUESTED`, handleProjectRoles),
    takeLatest(saveRoleRoutine.FULFILLED, postSaveRedirect),
    takeLatest(updateRoleRoutine.FULFILLED, postSaveRedirect),
    takeLatest(removeRoleRoutine.FULFILLED, postSaveRedirect),
    takeLatest(saveRoleRoutine.FULFILLED, showMessageSaga('info', 'Role saved successfully', 2000)),
    takeLatest(updateRoleRoutine.FULFILLED, showMessageSaga('info', 'Role saved successfully', 2000)),
    takeLatest(removeRoleRoutine.FULFILLED, showMessageSaga('info', 'Role removed successfully', 2000)),
    takeLatest(saveRoleRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Saving', 'role'), 5000)),
    takeLatest(updateRoleRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Updating', 'role'), 5000)),
    takeLatest(removeRoleRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Removing', 'role'), 5000))
  ]);
}
